import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

/**
 * "What Recipients Are Saying" — emotional reactions from people who
 * received a Living Announcement. Distinct from sender testimonials —
 * these are about the moment of receiving, not the product.
 *
 * Honors diversity memory (mix of relationships, geographies, ages).
 */

interface Reaction {
  quote: string;
  attribution: string;
  chip: string;
}

const REACTIONS: Reaction[] = [
  {
    quote: "I cried before I finished reading.",
    attribution: "Maria · received from her son · 58, Texas",
    chip: "Birthday",
  },
  {
    quote: "I watched the video six times that first night. I'm still watching it.",
    attribution: "Daniel · received from his daughter · 62, Lagos",
    chip: "Legacy",
  },
  {
    quote: "I never knew my father felt this way. I had to sit down.",
    attribution: "Priya · received from her dad · 34, Mumbai",
    chip: "Appreciation",
  },
  {
    quote: "This became my favorite gift. I keep coming back to it.",
    attribution: "Linda · received from her team · 67, retired teacher",
    chip: "Retirement",
  },
  {
    quote: "It said the things we never knew how to say out loud.",
    attribution: "Hiroshi · received from his brother · 41, Osaka",
    chip: "Apology",
  },
  {
    quote: "I forwarded it to my kids. I want them to know who their grandmother was.",
    attribution: "Carmen · received from her partner · 71, Mexico City",
    chip: "Anniversary",
  },
];

interface Props {
  /** Optional hook for a future user-submitted reaction form. */
  onSubmitReaction?: () => void;
}

export function RecipientReactions({ onSubmitReaction }: Props) {
  return (
    <section
      className="relative py-20 sm:py-28"
      aria-label="What recipients are saying"
    >
      <div className="container max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00C896] mb-4 font-medium">
            From the people who received one
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-foreground mb-4">
            What recipients are saying
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            These are the moments most senders never get to see. We did.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {REACTIONS.map((r, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="relative rounded-2xl bg-card/40 backdrop-blur border border-border/50 p-6 sm:p-7 hover:border-[#00C896]/40 transition-colors"
            >
              <Quote className="absolute top-4 right-4 w-6 h-6 text-[#00C896]/30" />
              <blockquote className="font-serif italic text-lg sm:text-xl leading-relaxed text-foreground/95 mb-5">
                "{r.quote}"
              </blockquote>
              <figcaption className="flex items-center justify-between gap-3 pt-4 border-t border-border/40">
                <span className="text-xs text-muted-foreground leading-snug">
                  {r.attribution}
                </span>
                <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#00C896]/10 text-[#00C896] font-medium">
                  {r.chip}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-12 text-center">
          {onSubmitReaction ? (
            <button
              onClick={onSubmitReaction}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Share your reaction →
            </button>
          ) : (
            <a
              href="mailto:hello@say-it-now.app?subject=My%20Living%20Announcement%20reaction"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Share your reaction →
            </a>
          )}
          <p className="text-xs text-muted-foreground/70 mt-2 italic">
            Reactions shared with permission. Names and details may be adjusted for privacy.
          </p>
        </div>
      </div>
    </section>
  );
}
